import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReportHeader from './components/ReportHeader';
import LoanSummary from './components/LoanSummary';
import AmortizationTable from './components/AmortizationTable';
import PrintActionBar from './components/PrintActionBar';
import ReportFooter from './components/ReportFooter';
import NoDataState from './components/NoDataState';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';



const PrintPreviewReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loanData, setLoanData] = useState(null);
  const [scheduleData, setScheduleData] = useState([]);
  const [calculationDate, setCalculationDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [hasRealData, setHasRealData] = useState(false);

  // Mock loan data - in real app this would come from props or context
  const mockLoanData = {
    loanAmount: 250000,
    downPayment: 50000,
    loanClosingDate: '2025-01-15',
    annualInterestRate: 6.5,
    totalPayments: 360,
    monthlyPayment: 1580.17,
    totalInterest: 318861.20,
    totalCost: 568861.20,
    paymentFrequency: 'monthly',
    compounding: 'monthly',
    firstPaymentDate: '2025-02-01',
    payoffDate: new Date('2055-01-01'),
    points: 1,
    pointsCost: 2500,
    loanDescription: 'Sample Home Mortgage Loan'
  };

  // Generate amortization schedule with proper calculations
  const generateAmortizationSchedule = (loanData) => {
    const schedule = [];
    let remainingBalance = loanData?.loanAmount || loanData?.principal;
    let paymentNumber = 1;
    
    // Fix date parsing to ensure correct payment dates
    let currentDate;
    const startDateString = loanData?.firstPaymentDate || '2025-02-01';
    
    if (typeof startDateString === 'string') {
      // Parse date string carefully to avoid timezone offset issues
      if (startDateString?.includes('T')) {
        // ISO string format
        currentDate = new Date(startDateString.split('T')[0] + 'T12:00:00.000Z');
      } else if (startDateString?.includes('-')) {
        // YYYY-MM-DD format - create date in local timezone
        const dateParts = startDateString?.split('-');
        currentDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]), 12, 0, 0);
      } else {
        currentDate = new Date(startDateString + 'T12:00:00.000Z');
      }
    } else {
      currentDate = new Date(startDateString);
    }
    
    // Calculate the correct periodic rate based on payment frequency
    const paymentFrequency = loanData?.paymentFrequency || 'monthly';
    const paymentsPerYear = paymentFrequency === 'monthly' ? 12 : 
                           paymentFrequency === 'bi-weekly' ? 26 :
                           paymentFrequency === 'semi-monthly' ? 24 :
                           paymentFrequency === 'quarterly' ? 4 : 1;
    const periodicRate = (loanData?.annualInterestRate || loanData?.interestRate) / paymentsPerYear / 100;
    const monthlyPayment = loanData?.monthlyPayment;
    const maxPayments = loanData?.totalPayments || 360;
    
    while (remainingBalance > 0.01 && paymentNumber <= maxPayments) {
      // Calculate interest payment using the correct periodic rate
      const interestPayment = remainingBalance * periodicRate;
      let principalPayment = monthlyPayment - interestPayment;
      let actualPayment = monthlyPayment;
      
      // Handle final payment
      if (principalPayment > remainingBalance) {
        principalPayment = remainingBalance;
        actualPayment = interestPayment + principalPayment;
      }
      
      remainingBalance -= principalPayment;
      
      schedule?.push({
        paymentNumber,
        paymentDate: new Date(currentDate.getTime()), // Create new date instance to avoid reference issues
        paymentAmount: actualPayment,
        interestPayment: Math.max(0, interestPayment),
        principalPayment: Math.max(0, principalPayment),
        remainingBalance: Math.max(0, remainingBalance)
      });
      
      // Move to next payment date based on frequency
      if (paymentFrequency === 'monthly') {
        currentDate?.setMonth(currentDate?.getMonth() + 1);
      } else if (paymentFrequency === 'bi-weekly') {
        currentDate?.setDate(currentDate?.getDate() + 14);
      } else if (paymentFrequency === 'semi-monthly') {
        // Semi-monthly: 1st and 15th of each month (or next business day)
        if (currentDate?.getDate() === 1) {
          currentDate?.setDate(15);
        } else {
          currentDate?.setMonth(currentDate?.getMonth() + 1);
          currentDate?.setDate(1);
        }
      } else if (paymentFrequency === 'quarterly') {
        currentDate?.setMonth(currentDate?.getMonth() + 3);
      } else { // annually
        currentDate?.setFullYear(currentDate?.getFullYear() + 1);
      }
      
      paymentNumber++;
    }
    
    return schedule;
  };

  useEffect(() => {
    const initializeReportData = () => {
      try {
        setIsLoading(true);
        setHasError(false);
        setHasRealData(false);

        // First, try to get data from location state (programmatic navigation)
        if (location?.state?.loanData) {
          const { loanData: stateLoanData } = location?.state;
          setLoanData(stateLoanData);
          setScheduleData(generateAmortizationSchedule(stateLoanData));
          setHasRealData(true);
          setIsLoading(false);
          return;
        }

        // Second, try to get data from localStorage
        const savedLoanData = localStorage.getItem('currentLoanCalculation');
        if (savedLoanData) {
          try {
            const parsedData = JSON.parse(savedLoanData);
            setLoanData(parsedData);
            setScheduleData(generateAmortizationSchedule(parsedData));
            setHasRealData(true);
            setIsLoading(false);
            return;
          } catch (parseError) {
            console.warn('Error parsing saved loan data:', parseError);
          }
        }

        // Third, try to get data from URL parameters (if implemented)
        const urlParams = new URLSearchParams(location.search);
        if (urlParams?.has('loanAmount')) {
          const urlLoanData = {
            loanAmount: parseFloat(urlParams?.get('loanAmount')) || mockLoanData?.loanAmount,
            annualInterestRate: parseFloat(urlParams?.get('rate')) || mockLoanData?.annualInterestRate,
            totalPayments: parseInt(urlParams?.get('term')) || mockLoanData?.totalPayments,
            monthlyPayment: parseFloat(urlParams?.get('payment')) || mockLoanData?.monthlyPayment,
            paymentFrequency: urlParams?.get('frequency') || mockLoanData?.paymentFrequency,
            loanDescription: urlParams?.get('description') || mockLoanData?.loanDescription
          };
          
          // Calculate derived values
          const principal = urlLoanData?.loanAmount;
          const monthlyRate = urlLoanData?.annualInterestRate / 100 / 12;
          const numPayments = urlLoanData?.totalPayments;
          
          if (monthlyRate > 0) {
            urlLoanData.monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
          } else {
            urlLoanData.monthlyPayment = principal / numPayments;
          }
          
          urlLoanData.totalInterest = (urlLoanData?.monthlyPayment * numPayments) - principal;
          urlLoanData.totalCost = principal + urlLoanData?.totalInterest;
          
          setLoanData(urlLoanData);
          setScheduleData(generateAmortizationSchedule(urlLoanData));
          setHasRealData(true);
          setIsLoading(false);
          return;
        }

        // Check if user explicitly wants to see sample report
        const showSample = urlParams?.get('sample') === 'true';
        if (showSample) {
          console.info('Showing sample report as requested');
          setLoanData(mockLoanData);
          setScheduleData(generateAmortizationSchedule(mockLoanData));
          setHasRealData(false);
          setIsLoading(false);
          return;
        }

        // If no data found, show no data state instead of using mock data
        setHasRealData(false);
        setIsLoading(false);

      } catch (error) {
        console.error('Error initializing report data:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    initializeReportData();

    // Set calculation date
    const now = new Date();
    setCalculationDate(now?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }));
  }, [location?.state, location?.search]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    // This is now handled by the PrintActionBar component
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('PDF export completed');
        resolve();
      }, 1500);
    });
  };

  const handleEmailShare = async () => {
    // In real app, this would open email client or send email
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Email sharing initiated');
        resolve();
      }, 1000);
    });
  };

  const handleBackToCalculator = () => {
    navigate('/loan-calculator-dashboard');
  };

  // Error state
  if (hasError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Unable to Load Report</h2>
          <p className="text-gray-600 mb-6">
            There was an error loading the loan calculation data. Please try again.
          </p>
          <button
            onClick={handleBackToCalculator}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Calculator
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  // No data state - show user-friendly message instead of 404 or mock data
  if (!hasRealData && !loanData) {
    return <NoDataState />;
  }

  // Show sample report if explicitly requested or as fallback
  if (!hasRealData && loanData) {
    return (
      <div className="min-h-screen bg-gray-100 print:bg-white">
        {/* Sample Report Banner */}
        <div className="bg-amber-50 border-b border-amber-200 p-4 print:hidden">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <Icon name="Info" size={16} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-amber-800 font-medium">Sample Report Preview</p>
                  <p className="text-amber-700 text-sm">This is a sample report. Create your own calculation to generate a personalized report.</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="Calculator"
                iconPosition="left"
                onClick={handleBackToCalculator}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                Create Calculation
              </Button>
            </div>
          </div>
        </div>

        {/* Print Action Bar */}
        <PrintActionBar
          onPrint={handlePrint}
          onExportPDF={handleExportPDF}
          onEmailShare={handleEmailShare}
          onBackToCalculator={handleBackToCalculator}
          loanData={loanData}
          isSampleData={true}
        />

        {/* Main Report Content */}
        <div className="max-w-7xl mx-auto p-6 print:p-0 print:max-w-none">
          {/* Print Document Container */}
          <div className="bg-white rounded-lg shadow-elevated print:shadow-none print:rounded-none min-h-[11in] print:min-h-0" id="print-preview-content">
            <div className="p-8 print:p-6">
              {/* Report Header */}
              <ReportHeader 
                loanData={loanData} 
                calculationDate={calculationDate}
              />

              {/* Loan Summary */}
              <LoanSummary 
                loanData={loanData}
              />

              {/* Amortization Table */}
              <AmortizationTable 
                scheduleData={scheduleData}
                showYearlySummary={true}
              />

              {/* Report Footer */}
              <ReportFooter 
                calculationDate={calculationDate}
                pageNumber={1}
                totalPages={1}
              />
            </div>
          </div>
        </div>

        {/* Mobile Bottom Spacing */}
        <div className="h-20 md:hidden print:hidden"></div>

        {/* Print Styles */}
        <style jsx>{`
          @media print {
            @page {
              size: letter;
              margin: 0.35in;
            }
            
            body {
              -webkit-print-color-adjust: exact;
              color-adjust: exact;
            }
            
            .print\\:break-inside-avoid {
              break-inside: avoid;
            }
            
            .print\\:break-before-page {
              break-before: page;
            }
            
            .print\\:break-after-page {
              break-after: page;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      {/* Print Action Bar */}
      <PrintActionBar
        onPrint={handlePrint}
        onExportPDF={handleExportPDF}
        onEmailShare={handleEmailShare}
        onBackToCalculator={handleBackToCalculator}
        loanData={loanData}
      />

      {/* Main Report Content */}
      <div className="max-w-7xl mx-auto p-6 print:p-0 print:max-w-none">
        {/* Print Document Container */}
        <div className="bg-white rounded-lg shadow-elevated print:shadow-none print:rounded-none min-h-[11in] print:min-h-0" id="print-preview-content">
          <div className="p-8 print:p-2">
            {/* Report Header */}
            <ReportHeader 
              loanData={loanData} 
              calculationDate={calculationDate}
            />

            {/* Loan Summary */}
            <LoanSummary 
              loanData={loanData}
            />

            {/* Amortization Table */}
            <AmortizationTable 
              scheduleData={scheduleData}
              showYearlySummary={true}
            />

            {/* Report Footer */}
            <ReportFooter 
              calculationDate={calculationDate}
              pageNumber={1}
              totalPages={1}
            />
          </div>
        </div>
      </div>

      {/* Mobile Bottom Spacing */}
      <div className="h-20 md:hidden print:hidden"></div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            size: letter;
            margin: 0.5in;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          .print\\:break-inside-avoid {
            break-inside: avoid;
          }
          
          .print\\:break-before-page {
            break-before: page;
          }
          
          .print\\:break-after-page {
            break-after: page;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintPreviewReport;